import React, { useState, useEffect, useRef } from 'react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toQuarterKey(centerDate) {
  if (!centerDate) return null;
  const d = new Date(centerDate.slice(0, 10) + 'T12:00:00Z');
  return `${d.getUTCFullYear()}-Q${Math.ceil((d.getUTCMonth() + 1) / 3)}`;
}

function parseQuarterKey(qk) {
  const match = qk && qk.match(/^(\d{4})-Q([1-4])$/);
  if (!match) return null;
  const year = parseInt(match[1]), qNum = parseInt(match[2]);
  return { quarterLabel: `Q${qNum} ${year}` };
}

function computeWindow(centerDate) {
  const center = new Date(centerDate.slice(0, 10) + 'T12:00:00Z');
  const start  = new Date(center); start.setFullYear(start.getFullYear() - 1);
  const end    = new Date(center); end.setDate(end.getDate() + 90);
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

// ── Prompts (mirrored from api/_lib/news.js for browser-side OpenAI calls) ───

const NEWS_SYSTEM_PROMPT = `You are a financial intelligence analyst. You will research a public company across a specific time window and return a comprehensive structured analysis covering media sentiment, public sentiment, corporate actions, earnings analysis, CEO quotes, analyst statements, competitive positioning, and macro/sector themes.

You MUST return your final answer as a single valid JSON object (no markdown fences, no text before or after the JSON) matching the exact schema provided in the user message.
Use web_search to find information. Constrain your searches to the specified date range.
If you cannot find information for a category, return an empty array or null for that field and set the relevant noData flag to true.`;

function buildNewsUserMessage(ticker, companyName, quarterKey, quarterLabel, startDate, endDate) {
  return `Research ${companyName || ticker} (ticker: ${ticker}) for the period ${startDate} to ${endDate}.
This corresponds to the 13F filing quarter ${quarterLabel}.

Find and analyze:
1. MEDIA SENTIMENT: What major financial media (Bloomberg, WSJ, Reuters, FT, CNBC, Seeking Alpha) said about ${ticker} during this period.
2. PUBLIC/RETAIL SENTIMENT: Retail investor mood from Reddit (r/investing, r/stocks, WSB), social media, and StockTwits.
3. CORPORATE ACTIONS: Any acquisitions, divestitures, partnerships, buybacks, or major leadership changes announced between ${startDate} and ${endDate}.
4. EARNINGS & MARGIN SENTIMENT: Analyst/press reaction to quarterly earnings reports, margin trends, and guidance changes in this period.
5. CEO & LEADERSHIP QUOTES: Key public statements from CEO/CFO in earnings calls, investor days, or press interviews during this period.
6. TOP ANALYST STATEMENTS: Price target changes, rating upgrades/downgrades, and key analyst commentary from major banks/firms.
7. COMPETITIVE ANALYSIS: How ${ticker}'s growth compares to sector peers — organic vs inorganic growth, market share trajectory, product/service expansion.
8. MACRO & SECTOR VIEW: What macro trends the company is riding or disrupting, which adjacent sectors it is touching or threatening.

Return ONLY this JSON (no markdown, no explanation outside the JSON):
{
  "ticker": "${ticker}",
  "quarterKey": "${quarterKey}",
  "quarterLabel": "${quarterLabel}",
  "windowStart": "${startDate}",
  "windowEnd": "${endDate}",
  "summary": "3-4 sentence overall narrative for this period",
  "overallSentiment": "bullish|bearish|mixed|neutral",
  "mediaSentiment": { "tone": "positive|negative|mixed|neutral", "summary": "...", "sources": [{ "outlet": "...", "headline": "...", "date": "YYYY-MM-DD", "tone": "positive|negative|neutral" }] },
  "publicSentiment": { "tone": "positive|negative|mixed|neutral", "summary": "...", "platforms": [{ "platform": "Reddit|Twitter|StockTwits", "tone": "...", "note": "..." }] },
  "corporateActions": [{ "type": "acquisition|divestiture|partnership|buyback|leadership|other", "description": "...", "date": "YYYY-MM-DD", "significance": "high|medium|low" }],
  "earningsSentiment": { "summary": "...", "revenueVsEstimate": "beat|miss|inline|unknown", "epsVsEstimate": "beat|miss|inline|unknown", "marginTrend": "expanding|contracting|stable|unknown", "guidanceTone": "raised|lowered|maintained|withdrawn|unknown" },
  "ceoQuotes": [{ "quote": "...", "context": "earnings call|investor day|interview|other", "date": "YYYY-MM-DD" }],
  "analystStatements": [{ "firm": "...", "analyst": "...", "action": "upgrade|downgrade|initiate|maintain", "targetPrice": "...", "rating": "...", "note": "...", "date": "YYYY-MM-DD" }],
  "competitiveAnalysis": { "growthType": "organic|inorganic|mixed", "summary": "...", "vsPeers": [{ "peer": "...", "comparison": "..." }] },
  "macroView": { "summary": "...", "tailwinds": ["..."], "headwinds": ["..."], "sectorsDisrupting": ["..."], "sectorsTouching": ["..."] },
  "stockReaction": { "direction": "up|down|flat", "magnitude": "e.g. +3.3% week after earnings", "context": "..." },
  "noResults": false,
  "parseError": false
}`;
}

function parseNewsJson(text) {
  const m = text.match(/\{[\s\S]*\}/);
  try { return m ? JSON.parse(m[0]) : null; } catch { return null; }
}

function fallbackNewsJson(rawText, ticker, quarterKey, quarterLabel, startDate, endDate) {
  return {
    ticker, quarterKey, quarterLabel,
    windowStart: startDate, windowEnd: endDate,
    summary: rawText.slice(0, 800),
    parseError: true, noResults: false,
    mediaSentiment: null, publicSentiment: null, corporateActions: [],
    earningsSentiment: null, ceoQuotes: [], analystStatements: [],
    competitiveAnalysis: null, macroView: null, stockReaction: null,
  };
}

// ── Browser-side OpenAI call (bypasses Vercel 10s timeout entirely) ───────────

async function fetchNewsFromBrowser(ticker, companyName, quarterKey, centerDate, openaiKey) {
  const { quarterLabel } = parseQuarterKey(quarterKey) || { quarterLabel: quarterKey };
  const { startDate, endDate } = computeWindow(centerDate);

  const r = await fetch('https://api.openai.com/v1/responses', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${openaiKey}`,
      'Content-Type':  'application/json',
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      tools: [{ type: 'web_search_preview' }],
      input: [
        { role: 'system', content: NEWS_SYSTEM_PROMPT },
        { role: 'user',   content: buildNewsUserMessage(ticker, companyName, quarterKey, quarterLabel, startDate, endDate) },
      ],
    }),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error?.message || `OpenAI error: HTTP ${r.status}`);
  }

  const data     = await r.json();
  const lastText = data.output_text || '';
  const structured = parseNewsJson(lastText) ||
    fallbackNewsJson(lastText, ticker, quarterKey, quarterLabel, startDate, endDate);

  return { structured, startDate, endDate };
}

function fmtWindowDate(iso) {
  if (!iso) return '';
  return new Date(iso.slice(0, 10) + 'T12:00:00Z')
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso.slice(0, 10) + 'T12:00:00Z')
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Color helpers ─────────────────────────────────────────────────────────────

const SENTIMENT_COLOR = {
  bullish:  '#00D4AA',
  positive: '#00D4AA',
  bearish:  '#FF6B6B',
  negative: '#FF6B6B',
  mixed:    '#FFEAA7',
  neutral:  '#8b949e',
};

const ACTION_COLOR = { upgrade: '#00D4AA', initiate: '#00D4AA', downgrade: '#FF6B6B', maintain: '#8b949e' };
const BEAT_COLOR   = { beat: '#00D4AA', miss: '#FF6B6B', inline: '#58a6ff', unknown: '#8b949e', expanding: '#00D4AA', contracting: '#FF6B6B', stable: '#8b949e', raised: '#00D4AA', lowered: '#FF6B6B', maintained: '#8b949e', withdrawn: '#FFEAA7' };
const SIG_COLOR    = { high: '#FF6B6B', medium: '#FFEAA7', low: '#8b949e' };

function Badge({ label, color }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.4px',
      background: (color || '#8b949e') + '22',
      color: color || '#8b949e',
      border: `1px solid ${(color || '#8b949e')}44`,
    }}>{label}</span>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.5px', color: '#58a6ff', marginBottom: 8,
      paddingBottom: 4, borderBottom: '1px solid #21262d',
    }}>{title}</div>
    {children}
  </div>
);

// ── Idle placeholder ──────────────────────────────────────────────────────────

function IdlePrompt({ ticker, companyName, centerDate, apiKey, setApiKey, apiProvider, setApiProvider, onFetch }) {
  const quarterKey = toQuarterKey(centerDate);
  if (!ticker) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#8b949e', fontSize: 11 }}>
        Click a holding to fetch news &amp; analysis
      </div>
    );
  }

  // Compute window dates client-side for display
  const center = new Date(centerDate.slice(0, 10) + 'T12:00:00Z');
  const start  = new Date(center); start.setFullYear(start.getFullYear() - 1);
  const end    = new Date(center); end.setDate(end.getDate() + 90);
  const windowStr = `${fmtWindowDate(start.toISOString())} → ${fmtWindowDate(end.toISOString())}`;

  return (
    <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#e6edf3' }}>{companyName || ticker}</div>
      <div style={{ fontSize: 11, color: '#8b949e' }}>{quarterKey} &nbsp;·&nbsp; {windowStr}</div>

      {/* API key input */}
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Provider toggle */}
          {['anthropic', 'openai'].map(p => (
            <button
              key={p}
              onClick={() => setApiProvider(p)}
              style={{
                padding: '4px 12px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'monospace',
                background: apiProvider === p ? '#1f6feb22' : 'transparent',
                border: `1px solid ${apiProvider === p ? '#388bfd' : '#30363d'}`,
                color: apiProvider === p ? '#58a6ff' : '#8b949e',
              }}
            >
              {p === 'anthropic' ? 'Claude' : 'ChatGPT'}
            </button>
          ))}
        </div>
        <input
          type="password"
          placeholder={apiProvider === 'anthropic' ? 'sk-ant-api03-…' : 'sk-proj-…'}
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          style={{
            background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
            color: '#e6edf3', fontSize: 11, padding: '7px 10px', fontFamily: 'monospace',
            outline: 'none', width: '100%', boxSizing: 'border-box',
          }}
        />
        <div style={{ fontSize: 10, color: '#8b949e', lineHeight: 1.5 }}>
          Key used once for this fetch · not stored · result cached for all users permanently
        </div>
      </div>

      <button
        onClick={onFetch}
        disabled={!apiKey.trim()}
        style={{
          marginTop: 4,
          background: apiKey.trim() ? '#1f6feb' : '#21262d',
          border: `1px solid ${apiKey.trim() ? '#388bfd' : '#30363d'}`,
          color: apiKey.trim() ? '#fff' : '#8b949e',
          padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 600,
          cursor: apiKey.trim() ? 'pointer' : 'not-allowed', fontFamily: 'monospace',
        }}
      >
        Fetch News Analysis
      </button>
    </div>
  );
}

// ── Loading state ─────────────────────────────────────────────────────────────

function LoadingState({ ticker, windowStart, windowEnd, provider }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '40px 0' }}>
      <div style={{ width: 24, height: 24, border: '3px solid #21262d', borderTop: '3px solid #58a6ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 12, color: '#e6edf3' }}>Researching {ticker}{provider ? ` via ${provider}` : ''}…</div>
      {windowStart && windowEnd && (
        <div style={{ fontSize: 10, color: '#8b949e' }}>{fmtWindowDate(windowStart)} → {fmtWindowDate(windowEnd)}</div>
      )}
      <div style={{ fontSize: 10, color: '#8b949e' }}>This may take 30–60 seconds</div>
    </div>
  );
}

// ── Error state ───────────────────────────────────────────────────────────────

function ErrorState({ error, noApiKey, onRetry }) {
  if (noApiKey) {
    return (
      <div style={{ margin: '16px 0', padding: 16, background: '#2d1b00', border: '1px solid #f0883e55', borderRadius: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#f0883e', marginBottom: 6 }}>API Key Required</div>
        <div style={{ fontSize: 11, color: '#e6edf3', lineHeight: 1.6 }}>
          Enter your API key above to fetch news analysis.<br />
          <span style={{ color: '#8b949e' }}>
            Claude key → console.anthropic.com &nbsp;·&nbsp; ChatGPT key → platform.openai.com
          </span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ margin: '16px 0', padding: 16, background: '#2d1313', border: '1px solid #FF6B6B55', borderRadius: 6 }}>
      <div style={{ fontSize: 12, color: '#FF6B6B', marginBottom: 8 }}>{error}</div>
      <button
        onClick={onRetry}
        style={{ background: '#21262d', border: '1px solid #30363d', color: '#e6edf3', padding: '6px 14px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
      >
        Retry
      </button>
    </div>
  );
}

// ── Full results display ──────────────────────────────────────────────────────

function NewsResults({ data, source }) {
  return (
    <div style={{ fontSize: 11, color: '#e6edf3' }}>

      {/* 1 — Summary strip */}
      <div style={{ background: '#0d1117', borderRadius: 6, padding: '12px 14px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Badge label={data.overallSentiment || 'unknown'} color={SENTIMENT_COLOR[data.overallSentiment] || '#8b949e'} />
          <span style={{ fontSize: 10, color: '#8b949e' }}>
            {fmtWindowDate(data.windowStart)} → {fmtWindowDate(data.windowEnd)}
          </span>
        </div>
        {data.summary && <div style={{ color: '#e6edf3', lineHeight: 1.6 }}>{data.summary}</div>}
      </div>

      {/* 2 — Media Sentiment */}
      {data.mediaSentiment && (
        <Section title="Media Sentiment">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Badge label={data.mediaSentiment.tone || 'unknown'} color={SENTIMENT_COLOR[data.mediaSentiment.tone]} />
            <span style={{ color: '#8b949e' }}>{data.mediaSentiment.summary}</span>
          </div>
          {data.mediaSentiment.sources?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
              {data.mediaSentiment.sources.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', borderBottom: '1px solid #21262d' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: SENTIMENT_COLOR[s.tone] || '#8b949e', flexShrink: 0, marginTop: 3 }} />
                  <span style={{ color: '#58a6ff', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 100 }}>{s.outlet}</span>
                  <span style={{ color: '#e6edf3', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.headline}>{s.headline}</span>
                  <span style={{ color: '#8b949e', whiteSpace: 'nowrap' }}>{s.date}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* 3 — Public / Retail Sentiment */}
      {data.publicSentiment && (
        <Section title="Public / Retail Sentiment">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Badge label={data.publicSentiment.tone || 'unknown'} color={SENTIMENT_COLOR[data.publicSentiment.tone]} />
            <span style={{ color: '#8b949e' }}>{data.publicSentiment.summary}</span>
          </div>
          {data.publicSentiment.platforms?.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {data.publicSentiment.platforms.map((p, i) => (
                <div key={i} style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 6, padding: '6px 10px', flex: '1 1 140px' }}>
                  <div style={{ fontWeight: 700, color: '#e6edf3', marginBottom: 2 }}>{p.platform}</div>
                  <div style={{ color: SENTIMENT_COLOR[p.tone] || '#8b949e', fontSize: 10, marginBottom: 2 }}>{p.tone}</div>
                  <div style={{ color: '#8b949e', fontSize: 10 }}>{p.note}</div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* 4 — Corporate Actions */}
      {data.corporateActions?.length > 0 && (
        <Section title="Corporate Actions">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.corporateActions.map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #21262d' }}>
                <Badge label={a.significance || 'low'} color={SIG_COLOR[a.significance] || '#8b949e'} />
                <Badge label={a.type || 'other'} color="#58a6ff" />
                <span style={{ flex: 1, color: '#e6edf3' }}>{a.description}</span>
                <span style={{ color: '#8b949e', whiteSpace: 'nowrap', fontSize: 10 }}>{fmtDate(a.date)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 5 — Earnings & Margins */}
      {data.earningsSentiment && (
        <Section title="Earnings &amp; Margins">
          <div style={{ color: '#8b949e', marginBottom: 8, lineHeight: 1.5 }}>{data.earningsSentiment.summary}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { label: 'Revenue vs Est.', val: data.earningsSentiment.revenueVsEstimate },
              { label: 'EPS vs Est.',     val: data.earningsSentiment.epsVsEstimate },
              { label: 'Margin Trend',    val: data.earningsSentiment.marginTrend },
              { label: 'Guidance',        val: data.earningsSentiment.guidanceTone },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: '#161b22', borderRadius: 6, padding: '8px 10px', border: '1px solid #21262d' }}>
                <div style={{ color: '#8b949e', fontSize: 10, marginBottom: 4 }}>{label}</div>
                <Badge label={val || 'unknown'} color={BEAT_COLOR[val] || '#8b949e'} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 6 — CEO & Leadership Quotes */}
      {data.ceoQuotes?.length > 0 && (
        <Section title="CEO &amp; Leadership">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.ceoQuotes.map((q, i) => (
              <div key={i} style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ color: '#e6edf3', fontStyle: 'italic', lineHeight: 1.5, marginBottom: 6 }}>"{q.quote}"</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge label={q.context || 'other'} color="#8b949e" />
                  <span style={{ color: '#8b949e', fontSize: 10 }}>{fmtDate(q.date)}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 7 — Analyst Statements */}
      {data.analystStatements?.length > 0 && (
        <Section title="Analyst Statements">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #21262d' }}>
                  {['Firm', 'Analyst', 'Action', 'Target', 'Rating', 'Note', 'Date'].map(h => (
                    <th key={h} style={{ padding: '4px 10px', color: '#8b949e', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.analystStatements.map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #21262d' }}>
                    <td style={{ padding: '5px 10px', color: '#e6edf3', fontWeight: 600, whiteSpace: 'nowrap' }}>{a.firm}</td>
                    <td style={{ padding: '5px 10px', color: '#8b949e', whiteSpace: 'nowrap' }}>{a.analyst || '—'}</td>
                    <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                      <Badge label={a.action || '—'} color={ACTION_COLOR[a.action] || '#8b949e'} />
                    </td>
                    <td style={{ padding: '5px 10px', color: '#e6edf3', whiteSpace: 'nowrap' }}>{a.targetPrice || '—'}</td>
                    <td style={{ padding: '5px 10px', color: '#8b949e', whiteSpace: 'nowrap' }}>{a.rating || '—'}</td>
                    <td style={{ padding: '5px 10px', color: '#8b949e', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.note}>{a.note || '—'}</td>
                    <td style={{ padding: '5px 10px', color: '#8b949e', whiteSpace: 'nowrap' }}>{a.date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* 8 — Competitive & Macro */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>

        {/* Competitive */}
        {data.competitiveAnalysis && (
          <Section title="Competitive Analysis">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Badge label={data.competitiveAnalysis.growthType || 'unknown'} color="#58a6ff" />
            </div>
            <div style={{ color: '#8b949e', marginBottom: 8, lineHeight: 1.5 }}>{data.competitiveAnalysis.summary}</div>
            {data.competitiveAnalysis.vsPeers?.map((p, i) => (
              <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #21262d' }}>
                <span style={{ color: '#e6edf3', fontWeight: 600 }}>{p.peer}: </span>
                <span style={{ color: '#8b949e' }}>{p.comparison}</span>
              </div>
            ))}
          </Section>
        )}

        {/* Macro */}
        {data.macroView && (
          <Section title="Macro &amp; Sector View">
            <div style={{ color: '#8b949e', marginBottom: 8, lineHeight: 1.5 }}>{data.macroView.summary}</div>
            {data.macroView.tailwinds?.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                {data.macroView.tailwinds.map((t, i) => (
                  <div key={i} style={{ color: '#00D4AA', fontSize: 10, padding: '2px 0' }}>▲ {t}</div>
                ))}
              </div>
            )}
            {data.macroView.headwinds?.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                {data.macroView.headwinds.map((h, i) => (
                  <div key={i} style={{ color: '#FF6B6B', fontSize: 10, padding: '2px 0' }}>▼ {h}</div>
                ))}
              </div>
            )}
            {(data.macroView.sectorsDisrupting?.length > 0 || data.macroView.sectorsTouching?.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {data.macroView.sectorsDisrupting?.map((s, i) => (
                  <Badge key={`d${i}`} label={s} color="#FFEAA7" />
                ))}
                {data.macroView.sectorsTouching?.map((s, i) => (
                  <Badge key={`t${i}`} label={s} color="#8b949e" />
                ))}
              </div>
            )}
          </Section>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #21262d', fontSize: 9, color: '#8b949e', display: 'flex', gap: 12 }}>
        <span>source: {source}</span>
        {data.provider && <span>via {data.provider === 'chatgpt' ? 'ChatGPT' : 'Claude'}</span>}
        {data.fetchedAt && <span>fetched {new Date(data.fetchedAt).toLocaleDateString()}</span>}
        <span>{fmtWindowDate(data.windowStart)} → {fmtWindowDate(data.windowEnd)}</span>
        {data.parseError && <span style={{ color: '#FFEAA7' }}>⚠ parse error — raw text shown</span>}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NewsPanel({ ticker, companyName, centerDate }) {
  const [status, setStatus]         = useState('idle');   // idle | loading | done | error
  const [data, setData]             = useState(null);
  const [source, setSource]         = useState(null);
  const [error, setError]           = useState(null);
  const [noApiKey, setNoApiKey]     = useState(false);
  const [apiKey, setApiKey]           = useState(() => sessionStorage.getItem('news_api_key') || '');
  const [apiProvider, setApiProvider] = useState(() => sessionStorage.getItem('news_api_provider') || 'anthropic');

  // Persist key + provider to sessionStorage
  const handleSetApiKey = (v) => { setApiKey(v); sessionStorage.setItem('news_api_key', v); };
  const handleSetApiProvider = (v) => { setApiProvider(v); sessionStorage.setItem('news_api_provider', v); };

  // Track which ticker+quarter is loaded so we can skip re-fetch on same combo
  const loadedKey = useRef(null);

  // Reset to idle when ticker or centerDate changes (unless already loaded for this key)
  useEffect(() => {
    const key = `${ticker}|${toQuarterKey(centerDate)}`;
    if (loadedKey.current === key && status === 'done') return;
    if (status === 'done' || status === 'error') {
      setStatus('idle');
      setData(null);
      setError(null);
      setNoApiKey(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, centerDate]);

  const quarterKey  = toQuarterKey(centerDate);

  // Compute window dates for display in loading state
  const windowDates = (() => {
    if (!centerDate) return {};
    const center = new Date(centerDate.slice(0, 10) + 'T12:00:00Z');
    const start  = new Date(center); start.setFullYear(start.getFullYear() - 1);
    const end    = new Date(center); end.setDate(end.getDate() + 90);
    return { windowStart: start.toISOString().slice(0, 10), windowEnd: end.toISOString().slice(0, 10) };
  })();

  const doFetch = () => {
    if (!ticker || !quarterKey) return;
    setStatus('loading');
    setError(null);
    setNoApiKey(false);

    const baseUrl = `/api/news/${encodeURIComponent(ticker)}/${encodeURIComponent(quarterKey)}`;
    const qs      = `centerDate=${centerDate?.slice(0, 10) || ''}&companyName=${encodeURIComponent(companyName || '')}`;
    const key     = apiKey.trim();

    // ── OpenAI path: check cache first, then call OpenAI directly from browser ──
    if (apiProvider === 'openai' && key) {
      fetch(`${baseUrl}?checkOnly=true&${qs}`)
        .then(async r => r.json())
        .then(async json => {
          // Cache hit — show immediately
          if (json.source === 'cache') {
            loadedKey.current = `${ticker}|${quarterKey}`;
            setData(json); setSource('cache'); setStatus('done');
            return;
          }
          // Cache miss — call OpenAI from browser (no server timeout)
          const { structured } = await fetchNewsFromBrowser(ticker, companyName, quarterKey, centerDate, key);
          const payload = { fetchedAt: new Date().toISOString(), provider: 'chatgpt', ...structured };
          // Store in KV via POST (fast, <1s)
          await fetch(baseUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: payload }),
          });
          loadedKey.current = `${ticker}|${quarterKey}`;
          setData({ source: 'fetch', ...payload });
          setSource('fetch');
          setStatus('done');
        })
        .catch(e => { setError(e.message); setStatus('error'); });
      return;
    }

    // ── Anthropic path: server-side call (unchanged) ───────────────────────────
    const headers = {};
    if (key) headers['x-anthropic-key'] = key;

    fetch(`${baseUrl}?${qs}`, { headers })
      .then(async r => {
        let json;
        try { json = await r.json(); }
        catch {
          if (r.status === 504) throw new Error('Request timed out — Claude news analysis requires Vercel Pro. Switch to ChatGPT (OpenAI key) to use the free tier.');
          throw new Error(`Server error (HTTP ${r.status})`);
        }
        if (!r.ok) {
          if (json.code === 'NO_API_KEY') { setNoApiKey(true); setStatus('error'); return; }
          throw new Error(json.error || `HTTP ${r.status}`);
        }
        loadedKey.current = `${ticker}|${quarterKey}`;
        setData(json);
        setSource(json.source);
        setStatus('done');
      })
      .catch(e => { setError(e.message); setStatus('error'); });
  };

  if (status === 'idle') {
    return <IdlePrompt
      ticker={ticker} companyName={companyName} centerDate={centerDate}
      apiKey={apiKey} setApiKey={handleSetApiKey}
      apiProvider={apiProvider} setApiProvider={handleSetApiProvider}
      onFetch={doFetch}
    />;
  }
  if (status === 'loading') {
    return <LoadingState ticker={ticker} {...windowDates}
      provider={apiProvider === 'openai' ? 'ChatGPT' : 'Claude'} />;
  }
  if (status === 'error') {
    return <ErrorState error={error} noApiKey={noApiKey} onRetry={doFetch} />;
  }
  return <NewsResults data={data} source={source} />;
}
